import { useGetAccountsList } from "@birdwatch/client";

import type { RelationshipType } from "@/types/openapi-enum";

import { toAccountFilterOption } from "../filter-options/account-filter-option";
import type { FilterOptionsResult } from "../types";
import { useDebouncedSearch } from "./use-debounced-search";

const PAGE_SIZE = 20;

/**
 * Account picker options for filter fields — debounced server search +
 * infinite paging over non-archived accounts, with `selectedOptions` resolved
 * by ids so chips label accounts not on the current page. Pass `relationship`
 * to scope the picker (e.g. customers only); omit it for all accounts.
 */
export function useAccountFilterOptions(ctx?: {
  selectedValues?: string[];
  relationship?: RelationshipType;
}): FilterOptionsResult {
  const selectedValues = ctx?.selectedValues ?? [];
  const { query, onSearch } = useDebouncedSearch();

  const accountFilters = {
    isArchived: "false",
    ...(ctx?.relationship ? { relationship: ctx.relationship } : {}),
  };

  const listResult = useGetAccountsList()
    .search(query || undefined)
    .take(PAGE_SIZE)
    .filters(accountFilters)
    .getInfinite();

  const selectedResult = useGetAccountsList()
    .filters({ ...accountFilters, ids: { in: selectedValues.join(",") } })
    .take(100)
    .get({ enabled: selectedValues.length > 0 });

  const accounts = listResult.data?.pages.flatMap((page) => page.data) ?? [];
  const selected = selectedValues.length ? (selectedResult.data ?? []) : [];

  return {
    options: accounts.map(toAccountFilterOption),
    selectedOptions: selected.map(toAccountFilterOption),
    isLoading: listResult.isLoading,
    onSearch,
    hasMore: listResult.hasNextPage,
    fetchMore: listResult.fetchNextPage,
  };
}
